import React, { useState, useEffect } from 'react';
import {
  Button,
  Label,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  FormText,
} from 'reactstrap';

/**
 * Modal to create or edit a service
 */
function ServiceModal({
  title = 'Servicio',
  isOpen = false,
  onToggle = () => {},
  defaultValue = null,
  onSubmit = () => {},
}) {
  const [titulo, setTitulo] = useState('');
  const [subtitulo, setSubtitulo] = useState('');
  const [precio, setPrecio] = useState('');
  const [categoria, setCategoria] = useState('Venta de Bienes');
  const [imagen, setImagen] = useState(null);
  const [preview, setPreview] = useState(null);

  // Update fields when defaultValue changes (for edit mode)
  useEffect(() => {
    if (defaultValue) {
      setTitulo(defaultValue.titulo || '');
      setSubtitulo(defaultValue.subtitulo || '');
      setPrecio(defaultValue.precio || '');
      setCategoria(defaultValue.categoria || 'Venta de Bienes');
      setPreview(defaultValue.imagen_url || null);
    } else {
      // Reset form for create mode
      setTitulo('');
      setSubtitulo('');
      setPrecio('');
      setCategoria('Venta de Bienes');
      setImagen(null);
      setPreview(null);
    }
  }, [defaultValue, isOpen]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImagen(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = () => {
    if (
      !titulo.trim() ||
      !subtitulo.trim() ||
      !precio.trim() ||
      !categoria.trim()
    ) {
      return;
    }

    // If editing and no new image, image is not required
    if (!defaultValue && !imagen) {
      return;
    }

    // Create FormData
    const formData = new FormData();
    formData.append('titulo', titulo.trim());
    formData.append('subtitulo', subtitulo.trim());
    formData.append('precio', precio.trim());
    formData.append('categoria', categoria.trim());

    // Only append image if it's a new file (for create) or if user uploaded a new one (for edit)
    if (imagen) {
      formData.append('imagen', imagen);
    }

    onSubmit(formData);
  };

  const handleCancel = () => {
    // Reset to original values
    if (defaultValue) {
      setTitulo(defaultValue.titulo || '');
      setSubtitulo(defaultValue.subtitulo || '');
      setPrecio(defaultValue.precio || '');
      setCategoria(defaultValue.categoria || 'Venta de Bienes');
      setPreview(defaultValue.imagen_url || null);
    } else {
      setTitulo('');
      setSubtitulo('');
      setPrecio('');
      setCategoria('Venta de Bienes');
      setImagen(null);
      setPreview(null);
    }
    onToggle(); // Close modal
  };

  return (
    <Modal isOpen={isOpen} fade={false} toggle={onToggle} size="lg">
      <ModalHeader toggle={onToggle}>{title}</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label htmlFor="titulo">Título *</Label>
          <Input
            id="titulo"
            type="text"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Ej: Servicio de Venta"
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="subtitulo">Subtítulo *</Label>
          <Input
            id="subtitulo"
            type="text"
            value={subtitulo}
            onChange={(e) => setSubtitulo(e.target.value)}
            placeholder="Ej: Descripción del servicio"
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="precio">Precio *</Label>
          <Input
            id="precio"
            type="text"
            value={precio}
            onChange={(e) => setPrecio(e.target.value)}
            placeholder="Ej: $100.000 o Consultar"
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="categoria">Categoría *</Label>
          <Input
            id="categoria"
            type="select"
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
          >
            <option value="Venta de Bienes">Venta de Bienes</option>
            <option value="Propiedades">Propiedades</option>
            <option value="Otros">Otros</option>
          </Input>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="imagen">
            Imagen {!defaultValue ? '*' : ''} (máx. 1200x800px)
          </Label>
          <Input
            id="imagen"
            type="file"
            accept="image/*"
            onChange={handleImageChange}
          />
          <FormText color="muted">
            La imagen será redimensionada automáticamente si es mayor a
            1200x800px
          </FormText>
          {preview && (
            <div className="mt-2">
              <img
                src={preview}
                alt="Preview"
                style={{ maxWidth: '100%', maxHeight: '300px' }}
              />
            </div>
          )}
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="success" onClick={handleSubmit}>
          Guardar
        </Button>
        <Button color="secondary" onClick={handleCancel}>
          Cancelar
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default ServiceModal;
